import { EnrichedFromBackendStopTrip } from './enrichment.service'

type TransportType = 'BUS' | 'TRAM' | undefined
export function mappedTransportType(transportType: string): TransportType {
  if (transportType === '0') {
    return 'BUS'
  } else if (transportType === '1') {
    return 'TRAM'
  } else {
    return undefined
  }
}

export type StatusLabel = {
  textColor: string
  backgroundColor: string
  text: string
}

export type DisplayPassage = {
  id: string
  destination: string
  viaDestination: string
  line: {
    publicNumber: string
    backgroundColor: string
    backgroundColorBorder: string
    color: string
    transportType: TransportType
  }
  plannedArrivalEpoch: number
  plannedDepartureEpoch: number
  realtimeArrivalEpoch?: number
  realtimeDepartureEpoch?: number
  merged?: boolean
  isLastStop: boolean
  isDeleted: boolean
  isDeparted: boolean
  isNotServed: boolean
  isRealTime: boolean
  isViaDestination: boolean
}

export function mapTriptoPassageWithLine(
  trip: EnrichedFromBackendStopTrip,
): DisplayPassage {
  const { lineDirection, passages } = trip
  const [passage] = passages

  const viaDestination =
    trip.realtimeViaDestination || trip.planningViaDestination
  return {
    id: trip.id,
    destination: trip.placeDestination,
    viaDestination: viaDestination || '',
    line: {
      publicNumber: lineDirection.publicLineNr,
      backgroundColor: lineDirection.lineColor.background,
      backgroundColorBorder: lineDirection.lineColor.backgroundBorder,
      color: lineDirection.lineColor.foreground,
      transportType: mappedTransportType(lineDirection.transportType),
    },
    plannedArrivalEpoch: passage.plannedPassage.arrivalEpoch,
    plannedDepartureEpoch: passage.plannedPassage.departureEpoch,
    realtimeArrivalEpoch: passage.realtimePassage?.arrivalEpoch,
    realtimeDepartureEpoch: passage.realtimePassage?.departureEpoch,
    merged: passage.merged,
    isLastStop: !!passage.lastStop,
    isDeleted: passage.realtimeStatuses
      ? passage.realtimeStatuses.some((status) =>
          ['CANCELLED', 'DELETED'].includes(status),
        )
      : passage.passageStatus === 'CANCELLED',
    isDeparted: !!passage.realtimeStatuses?.includes('DEPARTED'),
    isNotServed: passage.scheduleType === 'NOT_SERVED',
    isRealTime: !!passage.realtimeStatuses?.includes('REALTIME'),
    isViaDestination: !!viaDestination,
  }
}

export function getStatusLabels(passage: DisplayPassage): StatusLabel[] {
  const regularColorClasses = {
    text: 'text-neutral-550 dark:text-neutral-700 px-3',
    background: 'bg-neutral-300 dark:bg-neutral-400',
  }
  const redColorClasses = {
    text: 'text-red-50 px-3',
    background: 'bg-red-600 dark:bg-red-darkBg',
  }

  const statuses: StatusLabel[] = []

  if (passage.isDeleted) {
    return [
      {
        text: 'Rijdt niet',
        textColor: redColorClasses.text,
        backgroundColor: redColorClasses.background,
      },
    ]
  }

  if (passage.isViaDestination && !passage.isLastStop) {
    const textColor = passage.isNotServed
      ? 'text-red-600 dark:text-red-darkText font-medium'
      : 'text-neutral-550 dark:text-neutral-200 font-medium'
    statuses.push({
      text: passage.viaDestination,
      textColor,
      backgroundColor: '',
    })
  }

  if (passage.isNotServed) {
    statuses.push({
      text: 'In omleiding',
      textColor: redColorClasses.text,
      backgroundColor: redColorClasses.background,
    })
  }

  if (!passage.isRealTime && !passage.isNotServed) {
    statuses.push({
      text: 'Geen tracking info',
      textColor: regularColorClasses.text,
      backgroundColor: regularColorClasses.background,
    })
  }

  if (passage.isDeparted) {
    statuses.push({
      text: 'Reeds vertrokken',
      textColor: regularColorClasses.text,
      backgroundColor: regularColorClasses.background,
    })
  }

  if (passage.isLastStop) {
    statuses.push({
      text: 'Eindhalte',
      textColor: regularColorClasses.text,
      backgroundColor: regularColorClasses.background,
    })
  }

  return statuses
}

export function mapTripsToPassagesWithLine(
  trips: EnrichedFromBackendStopTrip[],
): DisplayPassage[] {
  // TypeScript wrongly infers the type after .filter(Boolean). As casting fixes this.
  return trips.map(mapTriptoPassageWithLine).filter(Boolean)
}

export function isTripValid(
  trip: DisplayPassage,
  includeEindHaltes: boolean,
  serverTijd: number,
) {
  if (!includeEindHaltes && trip.isLastStop) {
    return false
  }

  if (trip.merged) {
    return true
  }

  return (
    (trip.realtimeDepartureEpoch || trip.plannedDepartureEpoch) >= serverTijd
  )
}
